/**
 * The signature the letter will carry, shown inside the letter, under the text.
 *
 * The mailbox used to append its signature at send time and the composer said
 * nothing about it. Then it was a block pinned to the bottom of the window,
 * which read as part of the window rather than of the letter, and took writing
 * space from a short window. It now sits in the letter's own scrolling body,
 * right after the text (`RichTextEditor`'s `after`), and it is drawn from the
 * very HTML the send appends (`composerSignature` in the API), so what the
 * writer sees under their text is what the reader gets.
 *
 * IT IS NOT IN THE TEXT EDITOR. Typed into the body it would drift per letter,
 * and the thing a writer most wants - one sign-off that is always right - would
 * quietly stop being true. A click on it opens its fields in the same place
 * (`signatures/InlineSignatureEditor.tsx`), and Save changes the saved
 * signature. The pencil beside it picks another one, none, or opens all of the
 * writer's signatures and their sender (`signatures/MyMailDialog.tsx`).
 *
 * "No signature" is an option in the menu rather than a switch. A switch would
 * be a second control saying the same thing, next to a menu that is already the
 * answer to "which sign-off".
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { PenLine, Pencil, Settings2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { getMailSignatures, type ComposerSignature } from "../../../lib/mailSignature";
import { IconBtn } from "../../ui";
import { Pop, useClosePops } from "../../shell/Pop";
import { sanitizeMailHtml } from "./mailHtml";
import { InlineSignatureEditor } from "./signatures";

/** `null` is a deliberate "send without one", which is not the same as the
 *  writer having none. The composer keeps both states apart. */
export type SignatureChoice = string | null;

export interface SignatureSet {
  rows: ComposerSignature[];
  defaultId: string;
  /** The rows are the writer's own; false while they still use the shared ones. */
  own: boolean;
  /** A reload is on its way: the rows may be about to change. */
  loading: boolean;
  /** Ask again, after the writer changed their signatures. */
  reload: () => void;
}

/**
 * The writer's signatures, fetched once per composer and again on `reload`.
 *
 * Null while it is still asking, so the composer can leave the block out rather
 * than draw an empty one that fills in a moment later.
 */
export function useMailSignatures(): SignatureSet | null {
  const [data, setData] = useState<Omit<SignatureSet, "reload" | "loading"> | null>(null);
  const [round, setRound] = useState(0);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(() => {
    setLoading(true);
    setRound((n) => n + 1);
  }, []);
  useEffect(() => {
    let alive = true;
    getMailSignatures()
      .then((r) => alive && setData({ rows: r.signatures, defaultId: r.defaultSignatureId, own: r.own }))
      // A writer with no signatures and a list we could not ask for look the
      // same to the composer, and both are "nothing to pick" rather than an
      // error worth interrupting a letter for.
      .catch(() => alive && setData({ rows: [], defaultId: "", own: false }))
      .then(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [round]);
  return useMemo(() => (data ? { ...data, loading, reload } : null), [data, loading, reload]);
}

/** The label for one signature. The legacy single shared one arrives without a name. */
export function signatureLabel(sig: ComposerSignature, fallback: string): string {
  return sig.name.trim() || fallback;
}

/** The words of one signature for the menu: its grey text, or its card's lines. */
function excerpt(sig: ComposerSignature): string {
  if (sig.text.trim()) return sig.text;
  const card = sig.card;
  return card ? [card.closing, card.name, card.details].filter(Boolean).join("\n") : "";
}

export function SignatureBlock({
  set,
  value,
  onChange,
  onEdit,
  disabled,
}: {
  set: SignatureSet;
  value: SignatureChoice;
  onChange: (v: SignatureChoice) => void;
  /** Open the writer's own signatures. The composer draws that window outside
   *  its own, so the letter's keys (Escape, Cmd+Enter) do not reach it. */
  onEdit: () => void;
  disabled?: boolean;
}) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const current = value === null ? null : set.rows.find((s) => s.id === value) ?? null;
  // Our own markup, escaped field by field on the server; sanitised all the
  // same, as the reading pane does with the letters this CRM wrote.
  const html = useMemo(() => (current?.html ? sanitizeMailHtml(current.html, true) : ""), [current?.html]);

  // A choice that is no longer in the list (the writer just made their own
  // signatures, or removed the one picked) moves to the new default, so the
  // letter never shows one ending and sends another. Not while a reload is on
  // its way: a signature saved a moment ago is not in the old rows yet.
  const stale = typeof value === "string" && !current && !set.loading;
  useEffect(() => {
    if (stale) onChange(set.defaultId || null);
  }, [stale, set.defaultId, onChange]);

  // The own signature the click edits; a shared one, or none, starts a first own one.
  const editId = current && set.own ? current.id : null;

  function finishEdit(savedId: string | null) {
    setEditing(false);
    if (!savedId) return;
    set.reload();
    onChange(savedId);
  }

  if (editing) {
    return (
      <div className="px-[22px] pb-5">
        <InlineSignatureEditor editId={editId} onDone={finishEdit} />
      </div>
    );
  }

  return (
    <div className="group relative flex items-start gap-2 px-[22px] pb-5">
      {/* The signature itself is the way in: a click opens its fields here. */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        title={t("compose.signatureClickToEdit")}
        aria-label={t("compose.signatureEditHere")}
        className={cn(
          "-mx-2 min-w-0 flex-1 rounded-xl px-2 py-1 outline-none transition-colors",
          !disabled && "cursor-pointer hover:bg-[var(--crm-sunk)] focus-visible:bg-[var(--crm-sunk)]",
        )}
        onClick={() => !disabled && setEditing(true)}
        onKeyDown={(e) => {
          if (disabled || (e.key !== "Enter" && e.key !== " ")) return;
          e.preventDefault();
          setEditing(true);
        }}
      >
        {current ? (
          <>
            {/* `crm-mail-html` for the reading pane's rules: the card's inks are
                set for a white page, and the dark theme repaints them there. */}
            <div className="crm-mail-html text-[13.5px]" dangerouslySetInnerHTML={{ __html: html }} />
            {current.cardMissing ? (
              <p className="mt-2 text-[11.5px] leading-[1.5] text-muted-foreground/70">
                {t("compose.signatureCardEmpty")}
              </p>
            ) : null}
          </>
        ) : (
          <p className="text-[11.5px] leading-[1.5] text-muted-foreground/70">{t("compose.signatureNone")}</p>
        )}
      </div>

      <Pop
        trigger={(open) => (
          <IconBtn
            small
            className={cn("flex-none", open && "is-on")}
            title={t("compose.signature")}
            aria-label={t("compose.signature")}
            aria-expanded={open}
            disabled={disabled}
          >
            <Pencil className="size-3.5" />
          </IconBtn>
        )}
      >
        <SignatureMenu
          set={set}
          value={value}
          onChange={onChange}
          onEditHere={() => setEditing(true)}
          onEdit={onEdit}
        />
      </Pop>
    </div>
  );
}

function SignatureMenu({
  set,
  value,
  onChange,
  onEditHere,
  onEdit,
}: {
  set: SignatureSet;
  value: SignatureChoice;
  onChange: (v: SignatureChoice) => void;
  onEditHere: () => void;
  onEdit: () => void;
}) {
  const t = useT();
  const close = useClosePops();

  function pick(v: SignatureChoice) {
    onChange(v);
    close();
  }

  return (
    <div className="w-64 p-1">
      {/* The shared signatures are a stand-in until the writer makes their own,
          and the row under the list is where that is done. */}
      {set.rows.length === 0 ? (
        <p className="px-2.5 py-2 text-[11px] leading-snug text-muted-foreground">{t("compose.signatureNoneSaved")}</p>
      ) : !set.own ? (
        <p className="px-2.5 py-2 text-[11px] leading-snug text-muted-foreground">{t("compose.signatureShared")}</p>
      ) : null}
      {set.rows.map((sig) => (
        <button
          key={sig.id}
          type="button"
          className={cn(
            "block w-full rounded-lg px-2.5 py-2 text-left hover:bg-[var(--crm-sunk)]",
            value === sig.id && "bg-[var(--crm-sunk)]",
          )}
          onClick={() => pick(sig.id)}
        >
          <span className="block text-[12px] font-[600]">{signatureLabel(sig, t("compose.signatureUnnamed"))}</span>
          {/* The wording, three lines of it: the one place a writer can check
              what is about to be added under their message. */}
          <span className="mt-0.5 line-clamp-3 whitespace-pre-wrap text-[11px] leading-snug text-muted-foreground">
            {excerpt(sig)}
          </span>
          {sig.card ? (
            <span className="mt-0.5 block text-[11px] font-[550] text-muted-foreground">
              {t("compose.signatureCardLine")}
            </span>
          ) : null}
        </button>
      ))}
      <button
        type="button"
        className={cn(
          "block w-full rounded-lg px-2.5 py-2 text-left text-[12px] font-[600] hover:bg-[var(--crm-sunk)]",
          value === null && "bg-[var(--crm-sunk)]",
        )}
        onClick={() => pick(null)}
      >
        {t("compose.signatureNone")}
      </button>
      <div className="mx-2 my-1 h-px bg-[var(--crm-hair)]" />
      {/* Here, in the letter: the same as a click on the signature. */}
      <button
        type="button"
        className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[12px] font-[600] hover:bg-[var(--crm-sunk)]"
        onClick={() => {
          close();
          onEditHere();
        }}
      >
        <PenLine className="size-3.5 text-muted-foreground" />
        {t("compose.signatureEditHere")}
      </button>
      {/* Every signature and the default sender, in a window over the letter. */}
      <button
        type="button"
        className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[12px] font-[600] hover:bg-[var(--crm-sunk)]"
        onClick={() => {
          close();
          onEdit();
        }}
      >
        <Settings2 className="size-3.5 text-muted-foreground" />
        {t("myMail.edit")}
      </button>
    </div>
  );
}
